// Composer Studio — 케이스 편집(Record<string,string>) 직렬화 유틸
//
// CasesEditor(join/map/switch 등 cases 편집)는 편집 중간 상태(빈 키·중복 키)를
// Record로 즉시 직렬화하면 키 충돌로 행이 합쳐져 silent 소실된다. 따라서
// 로컬 CaseRow[] state를 source of truth로 두고(중복·빈 키 허용·순서 보존),
// 상위 format 반영 시에만 아래 직렬화/비교 함수를 사용한다.

/**
 * 케이스 편집 행 1건.
 *
 * `emptyKey`는 **빈 문자열 키('')를 저작자가 의도한 케이스**라는 표식이다. rc `map` 표시형식은
 * `hasOwnProperty(cases, toText(value))`로 매칭하므로 값이 비면(`''`) `cases['']`가 그대로 라벨이
 * 된다 — 위험성평가 결과서의 사진 칸 안내(`cases: { '': '사진 없음' }`)가 이 용법이다(resolver.ts
 * `case 'map'`). 종전 직렬화는 빈 키를 일괄 제외해 저작자가 CasesEditor를 열기만 해도 그 케이스가
 * 조용히 사라졌다(2026-09-14 은닉 감사 D-1). 반면 「+ 케이스 추가」 직후의 아직 키를 안 친 행도
 * 키가 ''라, 두 상태를 키 값만으로는 구분할 수 없어 표식을 따로 둔다.
 */
export interface CaseRow {
  key: string;
  value: string;
  /** true면 키 입력과 무관하게 '' 키로 직렬화한다(빈 값 케이스). 기본 false. */
  emptyKey?: boolean;
}

/**
 * Record<string,string> → 편집 행 목록. '' 키는 빈 값 케이스로 표식을 달아 라운드트립을 보장한다
 * (표식이 없으면 serializeCases가 「아직 키를 안 친 행」으로 보고 버린다).
 */
export function rowsFromCases(cases: Record<string, string>): CaseRow[] {
  return Object.entries(cases).map(([key, value]) =>
    key === '' ? { key, value, emptyKey: true } : { key, value },
  );
}

/**
 * 편집 행 목록을 Record<string,string>로 직렬화.
 * - `emptyKey` 행은 키 입력값과 무관하게 '' 키로 실린다(빈 값 케이스 보존).
 * - 그 외 빈 키 행은 제외(「+ 케이스 추가」 직후 미완성 행).
 * - 동일 키는 순서상 마지막 값 우선('' 키끼리도 같다).
 */
export function serializeCases(rows: CaseRow[]): Record<string, string> {
  const obj: Record<string, string> = {};
  for (const row of rows) {
    if (row.emptyKey) {
      obj[''] = row.value;
      continue;
    }
    if (row.key !== '') obj[row.key] = row.value; // 빈 키 제외(동일 키는 순서상 마지막이 덮어씀).
  }
  return obj;
}

/** 얕은 비교(Record<string,string>) — 외부 cases 변화 감지용. */
export function casesShallowEqual(a: Record<string, string>, b: Record<string, string>): boolean {
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  for (const k of ak) {
    if (a[k] !== b[k]) return false;
  }
  return true;
}
